import { useState } from "react";
import { validateForm } from "../utils/validation";

interface FormData {
  mail: string;
  phone: string;
  shipping_givenname: string;
  shipping_surname: string;
  shipping_address1: string;
  shipping_address2: string;
  shipping_postalCode: string;
  shipping_city: string;
  billing_givenname: string;
  billing_surname: string;
  billing_address1: string;
  billing_address2: string;
  billing_postalCode: string;
  billing_city: string;
}

/**
 * Custom React hook to manage the state and validation of a checkout form.
 *
 * @param onNext - Callback function to be called when the form passes validation and proceeds to the next step.
 * @returns An object containing:
 * - `formData`: The current form data state.
 * - `setFormData`: Setter function for updating the form data.
 * - `shouldUseShippingAddressForBilling`: Boolean indicating if the shipping address should be used for billing.
 * - `setShouldUseShippingAddressForBilling`: Setter function for toggling the use of shipping address for billing.
 * - `handleInputChange`: Event handler for updating form data on input change.
 * - `handleNextStep`: Function to validate the form and proceed to the next step if valid.
 *
 * The hook handles validation for required fields and email format, and supports using the shipping address as the billing address.
 */
export const useCheckoutForm = (onNext: () => void) => {
  const [formData, setFormData] = useState<FormData>({
    mail: "",
    phone: "",
    shipping_givenname: "",
    shipping_surname: "",
    shipping_address1: "",
    shipping_address2: "",
    shipping_postalCode: "",
    shipping_city: "",
    billing_givenname: "",
    billing_surname: "",
    billing_address1: "",
    billing_address2: "",
    billing_postalCode: "",
    billing_city: "",
  });

  const [shouldUseShippingAddressForBilling, setShouldUseShippingAddressForBilling] = useState(true);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNextStep = () => {
    const validationResult = validateForm([
      { name: "mail", value: formData.mail, isRequired: true, isEmail: true },
      { name: "phone", value: formData.phone, isRequired: false },
      { name: "shipping_givenname", value: formData.shipping_givenname, isRequired: true },
      { name: "shipping_surname", value: formData.shipping_surname, isRequired: true },
      { name: "shipping_address1", value: formData.shipping_address1, isRequired: true },
      { name: "shipping_address2", value: formData.shipping_address2, isRequired: false },
      { name: "shipping_postalCode", value: formData.shipping_postalCode, isRequired: true },
      { name: "shipping_city", value: formData.shipping_city, isRequired: true },
      { name: "billing_givenname", value: formData.billing_givenname, isRequired: !shouldUseShippingAddressForBilling },
      { name: "billing_surname", value: formData.billing_surname, isRequired: !shouldUseShippingAddressForBilling },
      { name: "billing_address1", value: formData.billing_address1, isRequired: !shouldUseShippingAddressForBilling },
      { name: "billing_address2", value: formData.billing_address2, isRequired: false },
      { name: "billing_postalCode", value: formData.billing_postalCode, isRequired: !shouldUseShippingAddressForBilling },
      { name: "billing_city", value: formData.billing_city, isRequired: !shouldUseShippingAddressForBilling },
    ]);

    if (!validationResult.isValid) {
      alert("Bitte füllen Sie alle erforderlichen Felder korrekt aus.");
      console.warn("Fehlerhafte Felder:", validationResult.errors);
      return;
    }

    onNext();
  };

  return {
    formData,
    setFormData,
    shouldUseShippingAddressForBilling,
    setShouldUseShippingAddressForBilling,
    handleInputChange,
    handleNextStep,
  };
};